'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  legalNeed?: string;
  message?: string;
}

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    legalNeed: '',
    message: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [hideForm, setHideForm] = useState(false);

  // Validation functions
  const validateName = (value: string): string | undefined => {
    if (!value.trim()) return 'Name is required';
    if (value.trim().length < 2) return 'Name must be at least 2 characters';
    return undefined;
  };

  const validateEmail = (value: string): string | undefined => {
    if (!value.trim()) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return 'Please enter a valid email address';
    return undefined;
  };

  const validatePhone = (value: string): string | undefined => {
    if (value && value.trim()) {
      const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
      if (!phoneRegex.test(value.replace(/\s/g, ''))) {
        return 'Please enter a valid phone number';
      }
    }
    return undefined;
  };

  const validateLegalNeed = (value: string): string | undefined => {
    if (!value) return 'Please select a legal service';
    return undefined;
  };

  const validateMessage = (value: string): string | undefined => {
    if (!value.trim()) return 'Message is required';
    if (value.trim().length < 10) return 'Message must be at least 10 characters';
    return undefined;
  };

  const handleBlur = (fieldName: string) => {
    setTouched({ ...touched, [fieldName]: true });
    
    let fieldError: string | undefined;
    const value = formData[fieldName as keyof typeof formData];
    
    switch (fieldName) {
      case 'name':
        fieldError = validateName(value);
        break;
      case 'email':
        fieldError = validateEmail(value);
        break;
      case 'phone':
        fieldError = validatePhone(value);
        break;
      case 'legalNeed':
        fieldError = validateLegalNeed(value);
        break;
      case 'message':
        fieldError = validateMessage(value);
        break;
    }
    
    setErrors({ ...errors, [fieldName]: fieldError });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields before submission
    const newErrors: FormErrors = {
      name: validateName(formData.name),
      email: validateEmail(formData.email),
      phone: validatePhone(formData.phone),
      legalNeed: validateLegalNeed(formData.legalNeed),
      message: validateMessage(formData.message),
    };
    
    // Remove undefined errors
    Object.keys(newErrors).forEach(key => {
      if (newErrors[key as keyof FormErrors] === undefined) {
        delete newErrors[key as keyof FormErrors];
      }
    });
    
    setErrors(newErrors);
    
    // If there are errors, don't submit
    if (Object.keys(newErrors).length > 0) {
      return;
    }
    
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          access_key: 'dc30d518-07e7-439b-9e04-e8e0a2d25f96',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: `Legal Inquiry: ${formData.legalNeed} - ${formData.country}`,
          message: `Country of Interest: ${formData.country}\nLegal Service Needed: ${formData.legalNeed}\n\nMessage:\n${formData.message}`,
          to: 'international@cyfire.net'
        })
      });

      const result = await response.json();

      if (result.success) {
        setShowSuccessModal(true);
        setHideForm(true);
        
        // Reset form after 2 seconds
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            phone: '',
            country: '',
            legalNeed: '',
            message: '',
          });
          setErrors({});
          setTouched({});
          setHideForm(false);
        }, 2000);
        
        // Hide modal after 5 seconds
        setTimeout(() => {
          setShowSuccessModal(false);
        }, 5000);
      } else {
        setError('Failed to send message. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again later.');
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear error when user starts typing
    if (touched[name]) {
      let fieldError: string | undefined;
      
      switch (name) {
        case 'name':
          fieldError = validateName(value);
          break;
        case 'email':
          fieldError = validateEmail(value);
          break;
        case 'phone':
          fieldError = validatePhone(value);
          break;
        case 'legalNeed':
          fieldError = validateLegalNeed(value);
          break;
        case 'message':
          fieldError = validateMessage(value);
          break;
      }
      
      setErrors({ ...errors, [name]: fieldError });
    }
  };

  const countries = [
    'Germany', 'Thailand', 'UAE', 'USA', 'Poland', 'Greece', 
    'Taiwan', 'Philippines', 'Estonia', 'Switzerland', 'Liechtenstein', 'Luxembourg'
  ];

  const legalNeeds = [
    'Corporate Law', 'Company Formation', 'Intellectual Property', 
    'Tax Law', 'Trade & Customs', 'Compliance', 'Litigation'
  ];

  return (
    <>
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-card border-2 border-green-500 rounded-2xl p-8 max-w-md mx-4 shadow-2xl animate-in zoom-in duration-300">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Icon name="CheckCircleIcon" size={40} className="text-green-600" />
              </div>
              <h3 className="text-2xl font-serif text-foreground mb-2">Message Sent Successfully!</h3>
              <p className="text-muted-foreground mb-6">
                Thank you for reaching out. We'll respond within 24 hours.
              </p>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="px-6 py-2 bg-primary text-primary-foreground rounded-full font-semibold hover:bg-primary/90 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={`glass-card rounded-2xl p-6 md:p-12 max-w-4xl mx-auto border-2 border-primary/20 shadow-xl transition-opacity duration-300 ${
        hideForm ? 'opacity-50 pointer-events-none' : 'opacity-100'
      }`}>
        <h2 className="text-2xl md:text-3xl font-serif text-foreground mb-2">Get in Touch</h2>
        <p className="text-muted-foreground mb-8">
          Fill out the form below and we'll respond within 24 hours.
        </p>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-500 rounded-lg flex items-center gap-3">
            <Icon name="ExclamationCircleIcon" size={24} className="text-red-600" />
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={() => handleBlur('name')}
                className={`w-full px-4 py-3 md:py-3 h-12 md:h-auto rounded-lg border-2 bg-background text-foreground focus:outline-none focus:ring-2 transition-all ${
                  errors.name && touched.name
                    ? 'border-red-500 focus:ring-red-500/50 focus:border-red-500' :'border-primary/30 focus:ring-primary/50 focus:border-primary'
                }`}
                placeholder="John Doe"
              />
              {errors.name && touched.name && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={16} />
                  {errors.name}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={() => handleBlur('email')}
                className={`w-full px-4 py-3 md:py-3 h-12 md:h-auto rounded-lg border-2 bg-background text-foreground focus:outline-none focus:ring-2 transition-all ${
                  errors.email && touched.email
                    ? 'border-red-500 focus:ring-red-500/50 focus:border-red-500' :'border-primary/30 focus:ring-primary/50 focus:border-primary'
                }`}
                placeholder="john@example.com"
              />
              {errors.email && touched.email && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={16} />
                  {errors.email}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onBlur={() => handleBlur('phone')}
                className={`w-full px-4 py-3 md:py-3 h-12 md:h-auto rounded-lg border-2 bg-background text-foreground focus:outline-none focus:ring-2 transition-all ${
                  errors.phone && touched.phone
                    ? 'border-red-500 focus:ring-red-500/50 focus:border-red-500' :'border-primary/30 focus:ring-primary/50 focus:border-primary'
                }`}
                placeholder="+1 234 567 8900"
              />
              {errors.phone && touched.phone && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={16} />
                  {errors.phone}
                </p>
              )}
            </div>

            {/* Country */}
            <div>
              <label htmlFor="country" className="block text-sm font-medium text-foreground mb-2">
                Country of Interest
              </label>
              <select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="w-full px-4 py-3 md:py-3 h-12 md:h-auto rounded-lg border-2 border-primary/30 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
              >
                <option value="">Select a country</option>
                {countries.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Legal Need */}
          <div>
            <label htmlFor="legalNeed" className="block text-sm font-medium text-foreground mb-2">
              Legal Service Needed *
            </label>
            <select
              id="legalNeed"
              name="legalNeed"
              value={formData.legalNeed}
              onChange={handleChange}
              onBlur={() => handleBlur('legalNeed')}
              className={`w-full px-4 py-3 md:py-3 h-12 md:h-auto rounded-lg border-2 bg-background text-foreground focus:outline-none focus:ring-2 transition-all ${
                errors.legalNeed && touched.legalNeed
                  ? 'border-red-500 focus:ring-red-500/50 focus:border-red-500' :'border-primary/30 focus:ring-primary/50 focus:border-primary'
              }`}
            >
              <option value="">Select a service</option>
              {legalNeeds.map((need) => (
                <option key={need} value={need}>
                  {need}
                </option>
              ))}
            </select>
            {errors.legalNeed && touched.legalNeed && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={16} />
                {errors.legalNeed}
              </p>
            )}
          </div>

          {/* Message */}
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
              Your Message *
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              onBlur={() => handleBlur('message')}
              rows={6}
              className={`w-full px-4 py-3 rounded-lg border-2 bg-background text-foreground focus:outline-none focus:ring-2 transition-all resize-none ${
                errors.message && touched.message
                  ? 'border-red-500 focus:ring-red-500/50 focus:border-red-500' :'border-primary/30 focus:ring-primary/50 focus:border-primary'
              }`}
              placeholder="Tell us about your legal needs..."
            />
            {errors.message && touched.message && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={16} />
                {errors.message}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-primary text-primary-foreground py-4 rounded-lg font-semibold hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Icon name="PaperAirplaneIcon" size={20} />
                <span>Send Message</span>
              </>
            )}
          </button>
        </form>
      </div>
    </>
  );
}